import { MessageAndTopic } from '@cco/model';
import { merge, Observable, race, throwError, timer } from 'rxjs';
import { exhaustMap, filter, ignoreElements, switchMap, take, timeout } from 'rxjs/operators';

/**
 * Payload AWS IoT publishes on `$aws/events/subscriptions/{subscribed,unsubscribed}/{clientId}`.
 * @see https://docs.aws.amazon.com/iot/latest/developerguide/life-cycle-events.html
 */
export interface IotSubscriptionEvent {
  eventType?: 'subscribed' | 'unsubscribed';
  clientId?: string;
  timestamp?: number;
  topics?: string[];
}

export interface IotRequestDependencies<T> {
  /** Subscribing to the returned observable is what issues the MQTT SUBSCRIBE. */
  subscribe$: (topicFilter: string) => Observable<MessageAndTopic<T>>;
  publish$: (topic: string, payload: unknown) => Observable<void>;
  /** Subscription lifecycle events for this client.  May never emit. */
  subscriptionEvents$: Observable<MessageAndTopic<IotSubscriptionEvent>>;
}

export interface IotRequestOptions {
  /** Budget from subscribing to receiving the first response. */
  timeoutMs?: number;
  /** How long to wait for the `subscribed` event before publishing anyway. */
  subscribeReadyTimeoutMs?: number;
  /** How often to re-publish the request while waiting for a response. */
  republishIntervalMs?: number;
}

export const DEFAULT_IOT_REQUEST_OPTIONS: Required<IotRequestOptions> = {
  timeoutMs: 10_000,
  subscribeReadyTimeoutMs: 250,
  republishIntervalMs: 2_000,
};

export class IotRequestTimeoutError extends Error {
  constructor(
    readonly requestTopic: string,
    readonly responseTopicFilter: string,
    readonly timeoutMs: number,
  ) {
    super(
      `No response on "${ responseTopicFilter }" within ${ timeoutMs }ms of publishing to "${ requestTopic }".`,
    );
    this.name = 'IotRequestTimeoutError';
  }
}

/**
 * Issue an MQTT request/response exchange.
 *
 * The response subscription is established before anything is published, and is
 * held until the response arrives rather than being tied to the publish
 * settling.  Publishing before the broker has registered the subscription loses
 * the response outright: the reply travels back through two republishing
 * lambdas, so it can arrive either side of the SUBACK.
 */
export function iotRequest$<T>(
  dependencies: IotRequestDependencies<T>,
  responseTopicFilter: string,
  requestTopic: string,
  payload: unknown = {},
  options: IotRequestOptions = {},
): Observable<MessageAndTopic<T>> {
  const { timeoutMs, subscribeReadyTimeoutMs, republishIntervalMs } = {
    ...DEFAULT_IOT_REQUEST_OPTIONS,
    ...options,
  };

  // AWS IoT reports the subscription itself; the timer keeps the exchange
  // working if lifecycle events are ever unavailable.
  const subscriptionLive$ = race(
    dependencies.subscriptionEvents$.pipe(
      filter(event =>
        'subscribed' === event.message?.eventType
        && !!event.message?.topics?.includes(responseTopicFilter)),
    ),
    timer(subscribeReadyTimeoutMs),
  ).pipe(take(1));

  const publish$ = subscriptionLive$.pipe(
    switchMap(() => timer(0, republishIntervalMs)),
    // A shadow get is idempotent, so re-asking is safe if a response is lost.
    exhaustMap(() => dependencies.publish$(requestTopic, payload).pipe(take(1))),
    ignoreElements(),
  );

  // merge subscribes in order, so the SUBSCRIBE is issued before the publish
  // pipeline exists, and the response never waits on the publish emitting.
  return merge(dependencies.subscribe$(responseTopicFilter), publish$).pipe(
    take(1),
    timeout({
      first: timeoutMs,
      with: () =>
        throwError(() =>
          new IotRequestTimeoutError(requestTopic, responseTopicFilter, timeoutMs)),
    }),
  );
}
